import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "react-hot-toast";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, Check, RefreshCw } from "lucide-react";

export default function SecuritySettings() {
    const [isEnabled, setIsEnabled] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    
    // Setup state
    const [qrCodeUrl, setQrCodeUrl] = useState("");
    const [secretStr, setSecretStr] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const checkStatus = async () => {
        try {
            const { data } = await api.get("/2fa/status");
            setIsEnabled(data.isEnabled);
        } catch (error) {
            console.error("Failed to fetch 2FA status", error);
            toast.error("Could not load security status.");
        } finally {
            setIsChecking(false);
        }
    };

    useEffect(() => {
        checkStatus();
    }, []);

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/2fa/generate");
            setQrCodeUrl(data.qrCode);
            setSecretStr(data.secret);
        } catch (error) {
            toast.error("Failed to generate QR code.");
        } finally {
            setLoading(false);
        }
    };

    const handleEnable = async (e) => {
        e.preventDefault();
        if (otpCode.length !== 6) {
            toast.error("Please enter a valid 6-digit code.");
            return;
        }

        setLoading(true);
        try {
            await api.post("/2fa/enable", { code: otpCode });
            toast.success("Two-Factor Authentication Enabled!");
            setIsEnabled(true);
            setQrCodeUrl(""); // Clear setup state
            setSecretStr("");
            setOtpCode("");
        } catch (error) {
            toast.error(error.response?.data?.error || "Invalid code. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async () => {
        if (!window.confirm("Are you sure you want to disable 2FA? This will severely weaken your account security.")) return;
        
        setLoading(true);
        try {
            await api.post("/2fa/disable");
            toast.success("2FA Disabled.");
            setIsEnabled(false);
        } catch (error) {
            toast.error("Failed to disable 2FA.");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(secretStr);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Secret copied.");
    };

    if (isChecking) {
        return <div className="text-gray-500 animate-pulse">Checking security clearance...</div>;
    }

    return (
        <div className="max-w-2xl bg-[#0d0d0d] border border-[#222] rounded-2xl p-6 lg:p-8">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-[#222]">
                <div className={`p-4 rounded-full ${isEnabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                    {isEnabled ? <ShieldCheck className="w-8 h-8" /> : <ShieldAlert className="w-8 h-8" />}
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
                    <p className={`text-sm ${isEnabled ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {isEnabled ? "Status: Secure & Active" : "Status: Vulnerable (Not Enabled)"}
                    </p>
                </div>
            </div>

            {isEnabled ? (
                <div className="space-y-6">
                    <p className="text-gray-400">
                        Your admin account is currently protected by an authenticator app. You will be required to enter a one-time code every time you log in.
                    </p>
                    <button
                        onClick={handleDisable}
                        disabled={loading}
                        className="px-6 py-3 border border-red-900/50 text-red-500 rounded-xl hover:bg-red-900/20 transition-colors"
                    >
                        {loading ? "Disabling..." : "Disable 2FA (Not Recommended)"}
                    </button>
                </div>
            ) : (
                <div className="space-y-8">
                    {!qrCodeUrl ? (
                        <>
                            <p className="text-gray-400 text-sm leading-relaxed max-w-lg">
                                Enhance the security of your Central Command access by requiring a time-based code from an authenticator app (like Google Authenticator or Authy) when you log in.
                            </p>
                            <button
                                onClick={handleGenerate}
                                disabled={loading}
                                className="px-6 py-3 bg-brand-blue hover:bg-brand-blue/90 text-white font-medium rounded-xl flex items-center gap-2 transition-colors"
                            >
                                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                                Begin Setup
                            </button>
                        </>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-8">
                            {/* Step 1: Scan */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-white">1. Scan the QR Code</h3>
                                <p className="text-sm text-gray-500">
                                    Open your authenticator app and scan this image.
                                </p>
                                <div className="bg-white p-4 rounded-xl inline-block max-w-[200px]">
                                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-full h-auto" />
                                </div>
                                
                                <div className="pt-4">
                                    <p className="text-xs text-gray-500 mb-2 mt-4 md:mt-0">Can't scan? Use this setup key:</p>
                                    <button 
                                        onClick={copyToClipboard}
                                        className="w-full flex items-center justify-between p-3 bg-[#111] border border-[#222] rounded-lg hover:border-brand-blue/50 transition-colors text-left"
                                    >
                                        <code className="text-sm text-brand-blue font-mono">{secretStr}</code>
                                        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                                    </button>
                                </div>
                            </div>

                            {/* Step 2: Verify */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-white">2. Enter the Code</h3>
                                <p className="text-sm text-gray-500">
                                    Enter the 6-digit code generated by your app to verify the setup.
                                </p>
                                <form onSubmit={handleEnable} className="space-y-4">
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                            <KeyRound className="w-5 h-5" />
                                        </div>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={6}
                                            value={otpCode}
                                            onChange={(e) => setOtpCode(e.target.value)}
                                            placeholder="000000"
                                            required
                                            className="w-full px-4 py-3 pl-12 text-center text-xl tracking-widest font-mono bg-[#111] border border-[#333] rounded-xl focus:border-brand-blue focus:ring-1 focus:ring-brand-blue text-white outline-none transition-all placeholder:font-sans placeholder:text-sm placeholder:tracking-normal"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading || otpCode.length !== 6}
                                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
                                    >
                                        {loading ? "Verifying..." : "Verify & Enable"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
